import React, { useEffect, useRef, useState } from 'react';
import './options.style.scss';
import { __OptionProps, __OptionRef, __OptionButtonType, __OptionItemType } from './options.interface';
import SLPInput from 'utilities/slp/input/input.index';
import { TextDirection } from 'interface';
import HintText from '../hintText/hintText.index';
import QuestionImage from '../questionImage/questionImage.index';
import { extractTitle } from 'scripts/string';

export interface SLPOptionRef extends __OptionRef {}
export interface OptionItemType extends __OptionItemType {}
export { __OptionButtonType as SLPOptionButtonType };

const SLPOptions: React.ForwardRefRenderFunction<__OptionRef, __OptionProps> = (props: __OptionProps, ref) => {
    const [value, setValue] = useState<__OptionItemType[]>(props.defaultValue ? props.defaultValue : []);
    const [textValue, setTextValue] = useState<string>();
    const [error, setError] = useState<string>();
    const immediateValue = useRef<__OptionItemType[]>(value);
    const index = value.findIndex((item) => Number(item.value) === 1);
    const labelRef = useRef<HTMLLabelElement>(null);

    React.useImperativeHandle(ref, () => ({
        getValue: () => immediateValue.current,
        getValidation: () => (props.required === true ? value.length > 0 : true),
        setError: (text) => {
            setError(text);
        },
    }));

    const changeHandler = (e: __OptionItemType[]) => {
        setError(undefined);
        immediateValue.current = e;
        setValue(immediateValue.current);
        if (props.onChange) props.onChange(e);
    };

    useEffect(() => {
        if (props.onTextChange) props.onTextChange(textValue);
    }, [textValue]);

    useEffect(() => {
        if (labelRef.current && props.label) labelRef.current.innerHTML = extractTitle(props.label, false);
    }, [props.label]);

    function onSelectItem(item: __OptionItemType) {
        if (!props.multiSelect) {
            value.length > 0 && value[0].payload === item.payload ? changeHandler([]) : changeHandler([item]);
        } else {
            let temp = [...value];
            value.map((i) => i.payload).includes(item.payload)
                ? (temp = temp.filter((x) => x.payload !== item.payload))
                : temp.push(item);
            changeHandler(temp);
        }
    }

    return (
        <div
            className={`u-check-SLP-options ${props.vertical ? ' vertical' : ''} ${
                props.hasBorder ? ' bordered-options' : ''
            }  ${props.dir === TextDirection.rtl ? 'rtl-text' : ''}`}
        >
            <div className="input-header">
                {props.required && <span className="required-star">*</span>}
                <span className="counter-span">{props.counter ? props.counter + '- ' : ''}</span>
                <label ref={labelRef} style={{ color: props.labelColor }} />
            </div>
            <QuestionImage src={props.image?.file} />
            <HintText value={props.hint} />
            <div className="options-container">
                {props.items.map((item, index) =>
                    item.archive || item.isHidden ? null : (
                        <label
                            key={index}
                            className={`options ${
                                props.buttonStyle !== __OptionButtonType.radio ? ' checkbox' : ' radio'
                            } ${value.map((i) => i.payload).includes(item.payload) ? 'active' : ''}`}
                            onClick={() => onSelectItem(item)}
                            style={{
                                color: props.color,
                                border: value.includes(item) ? `${props.color + ' 1px solid'}` : '',
                            }}
                        >
                            <p dangerouslySetInnerHTML={{ __html: item.title }}></p>
                            <div
                                style={{
                                    background: value.includes(item) ? props.color : '',
                                    border: value.includes(item) ? `${props.color + ' 1px solid'}` : '',
                                }}
                            />
                        </label>
                    ),
                )}
                {index !== undefined && index > -1 && (
                    <SLPInput onEnter={props.next} onChange={(e) => setTextValue(e)} />
                )}
                {Array.from(Array(7).keys()).map((item, index) => (
                    <label
                        key={index}
                        className={`spacer options u-check-options mt-0 mb-0 pt-0 pb-0 d-none ${
                            props.vertical ? '' : ' d-flex '
                        } `}
                    />
                ))}
            </div>
            {error && <p className="options-error">{error}</p>}
        </div>
    );
};

export default React.forwardRef(SLPOptions);
